// Environment configuration for VozyMe Admin Dashboard

interface EnvironmentConfig {
  API_BASE_URL: string;
  GRAFANA_URL: string;
  PROMETHEUS_URL: string;
  REFRESH_INTERVAL: number;
  REALTIME_REFRESH_INTERVAL: number;
}

interface Config extends EnvironmentConfig {
  ENDPOINTS: {
    LOGIN: string;
    REFRESH: string;
    ANALYTICS_OVERVIEW: string;
    ANALYTICS_USERS: string;
  };
  STORAGE_KEYS: {
    AUTH_TOKEN: string;
    REFRESH_TOKEN: string;
    USER_INFO: string;
  };
  CHART_COLORS: {
    primary: string;
    success: string;
    warning: string;
    error: string;
    info: string;
    purple: string;
    pink: string;
    indigo: string;
    gray: string;
  };
  DASHBOARD: {
    AUTO_REFRESH: boolean;
    MAX_CHART_POINTS: number;
    DEFAULT_DATE_RANGE: number;
  };
}

type Environment = 'development' | 'production';

const envConfig: Record<Environment, EnvironmentConfig> = {
  development: {
    API_BASE_URL: 'http://localhost:8000',
    GRAFANA_URL: 'http://localhost:3001',
    PROMETHEUS_URL: 'http://localhost:9090',
    REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes
    REALTIME_REFRESH_INTERVAL: 30 * 1000, // 30 seconds
  },
  production: {
    API_BASE_URL: 'https://api.vozyme.eu', // Production API with HTTPS
    GRAFANA_URL: 'https://grafana.vozyme.eu', // Production Grafana (if deployed)
    PROMETHEUS_URL: 'https://prometheus.vozyme.eu', // Production Prometheus (if deployed)
    REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes
    REALTIME_REFRESH_INTERVAL: 60 * 1000, // 1 minute
  }
};

// Allow override via environment variable (REACT_APP_API_URL for local dev testing)
const getApiBaseUrl = (): string => {
  // Priority 1: Explicit environment variable override
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Priority 2: Use development API for local testing
  // This will be set when we build with REACT_APP_USE_LOCAL_API=true
  if (process.env.REACT_APP_USE_LOCAL_API === 'true') {
    return envConfig.development.API_BASE_URL;
  }

  const environment = (process.env.NODE_ENV || 'development') as Environment;
  return envConfig[environment].API_BASE_URL;
};

const environment = (process.env.NODE_ENV || 'development') as Environment;

const config: Config = {
  ...envConfig[environment],
  API_BASE_URL: getApiBaseUrl(),

  // API Endpoints
  ENDPOINTS: {
    // Auth
    LOGIN: '/api/v1/auth/login-email',
    REFRESH: '/api/v1/auth/refresh',

    // Analytics
    ANALYTICS_OVERVIEW: '/api/v1/analytics/overview',
    ANALYTICS_USERS: '/api/v1/analytics/users',
  },

  // Local Storage Keys
  STORAGE_KEYS: {
    AUTH_TOKEN: 'vozyme_admin_token',
    REFRESH_TOKEN: 'vozyme_admin_refresh_token',
    USER_INFO: 'vozyme_admin_user',
  },

  // Chart Colors (Bulgarian flag inspired + modern palette)
  CHART_COLORS: {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#06b6d4',
    purple: '#8b5cf6',
    pink: '#ec4899',
    indigo: '#6366f1',
    gray: '#6b7280',
  },

  // Dashboard Configuration
  DASHBOARD: {
    AUTO_REFRESH: true,
    MAX_CHART_POINTS: 30,
    DEFAULT_DATE_RANGE: 30, // days
  }
};

export default config;
